import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Users, Plus, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';

const StaffPage = () => {
  const { token } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'recruiter' });
  const [inviting, setInviting] = useState(false);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(`${API}/organization/staff`, { headers, withCredentials: true });
      setStaff(res.data.staff || []);
    } catch {} 
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleInvite = async () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) { toast.error('Name and email required'); return; }
    setInviting(true);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      const res = await axios.post(`${API}/organization/staff/invite`, inviteForm, { headers, withCredentials: true });
      toast.success(`Invited ${res.data.user.name}. Temp password: ${res.data.temp_password}`);
      setShowInvite(false);
      setInviteForm({ name: '', email: '', role: 'recruiter' });
      fetchStaff();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to invite'); }
    setInviting(false);
  };

  const removeStaff = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.delete(`${API}/organization/staff/${userId}`, { headers, withCredentials: true });
      toast.success('Staff removed');
      fetchStaff();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const roleMap = { client_admin: 'Admin', team_leader: 'Team Lead', recruiter: 'Recruiter' };
  const roleColors = { client_admin: 'bg-purple-100 text-purple-700', team_leader: 'bg-blue-100 text-blue-700', recruiter: 'bg-green-100 text-green-700' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Staff Management</h1>
            <p className="text-slate-500 mt-1">Manage your institution's team</p>
          </div>
          <Button onClick={() => setShowInvite(true)} data-testid="invite-staff-btn">
            <Plus className="w-4 h-4 mr-2" />Invite Staff
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
        ) : staff.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Email</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600">Role</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Joined</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(s => (
                      <tr key={s.user_id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                        <td className="px-4 py-3 text-slate-600">{s.email}</td>
                        <td className="px-4 py-3 text-center"><Badge className={roleColors[s.role] || 'bg-slate-100 text-slate-700'}>{roleMap[s.role] || s.role}</Badge></td>
                        <td className="px-4 py-3 hidden md:table-cell text-slate-500 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => removeStaff(s.user_id, s.name)} data-testid={`remove-staff-${s.user_id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No staff members yet</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowInvite(true)}>Invite your first team member</Button>
            </CardContent>
          </Card>
        )}

        {/* Invite Dialog */}
        <Dialog open={showInvite} onOpenChange={setShowInvite}>
          <DialogContent data-testid="invite-dialog">
            <DialogHeader><DialogTitle>Invite Staff Member</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Full Name</Label><Input value={inviteForm.name} onChange={(e) => setInviteForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="invite-name" /></div>
              <div><Label>Email</Label><Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="invite-email" /></div>
              <div>
                <Label>Role</Label>
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger className="mt-1" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recruiter">Recruiter</SelectItem>
                    <SelectItem value="team_leader">Team Leader</SelectItem>
                    <SelectItem value="client_admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-400">A temporary password (welcome123) will be assigned. The staff member should change it after first login.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
              <Button onClick={handleInvite} disabled={inviting} data-testid="invite-submit"><Mail className="w-4 h-4 mr-2" />{inviting ? 'Inviting...' : 'Send Invite'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StaffPage;
